import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { ActionIcon, Box, Center, Flex, Group, Loader, rem, Text, Transition } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconPencilPlus } from '@tabler/icons-react';
import { useFamily } from 'src/clients/hooks/useFamily';
import { ApiError } from 'src/error/ApiError';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { MaskedSidebarVideo } from 'src/UI/common/MaskedSidebarVideo';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  resolveSuitabilityHostClientId,
  suitabilityFlowLink,
} from 'src/suitability-report/prefill';
import { useAppContainerContext } from '../AppContainerContext';
import { ChatsView } from '../chats/ChatsView';
import { useChatScroll } from '../chat/hooks/useChatScroll';
import {
  ErrorMarker,
  isChatMessage,
  isClientDataProposal,
  isMeetingActionProposal,
  isMemoryProposal,
  isSkillProposal,
  isSuitabilityReportProposal,
  MessageSkill,
  ProposedAction,
} from '../chat/types';
import { useSiesta } from '../SiestaProvider';
import { SteeringActionContent } from '../steering/SteeringActionContent';
import { SiestaAction, SiestaActionType } from '../types';
import { useAppContainer } from '../useAppContainer';
import { ActionModeScrollArea } from './ActionModeScrollArea';
import { AIActionSet } from './AIActionSet';
import { ChatEmptyState } from './ChatEmptyState';
import { ChatScrollBlur } from './ChatScrollBlur';
import { DisplacedChatBanner } from './DisplacedChatBanner';
import { ExitFullScreenPill } from './ExitFullScreenPill';
import classes from './SiestaBar.module.css';
import { SiestaBusyGlow } from './SiestaBusyGlow';
import { ChatModeTabs, type ChatBarTab } from './ChatModeTabs';
import { resumeMessageFor } from './proposalResumeMessage';
import { VerifyOutcome } from '../chat/verify/types';
import { useMicrosoftEmailConnectionStatus } from 'src/common/hooks/useMicrosoftEmailConnectionStatus';
import { OutlookEmailContext } from './EmailDraftCard';
import { SiestaBarMessages } from './SiestaBarMessages';
import { SiestaChatInput } from './SiestaChatInput';
import { useSavedPresentedFiles } from './useSavePresentedFile';
import { useChatEmptyMessage } from './useChatEmptyMessage';
import { useRecordEditAction } from './useRecordEditAction';
import { useSuitabilityDecisionGuards } from './useSuitabilityDecisionGuards';
import { useOrphanProposalDecisions } from './useOrphanProposalDecisions';
import { useSuggestedActions } from './useSuggestedActions';
import { ChatFileDropzone } from './ChatFileDropzone';
import {
  chatFileExtension,
  chatFileRejection,
  chatFileRejectionMessage,
  chatFileRejectionsMessage,
  ChatFileRejection,
} from '../chats/chatFileTypes';
import { ChatSkill } from '../chats/skillCommands';
import { useConversationThinkingEffort } from '../chats/useConversationThinkingEffort';
import { useChatFileTypes } from '../chats/useChatFileTypes';
import { useChatSkills } from '../chats/useChatSkills';
import { useChatFiles } from '../chats/useChatFiles';
import { useConversations } from '../chats/useConversations';
import { useHistoryScope } from '../chats/useHistoryScope';
import { useMoveConversationToFamily } from '../chats/useMoveConversationToFamily';
import {
  toClientDataProposedAction,
  useClientDataProposalDecision,
  usePendingClientDataProposals,
} from '../chats/useClientDataProposals';
import {
  toMeetingActionProposedAction,
  useMeetingActionProposals,
  usePendingMeetingActionProposals,
} from '../chats/useMeetingActionProposals';
import {
  useSuitabilityReportProposalList,
  useSuitabilityReportProposals,
} from '../chats/useSuitabilityReportProposals';
import {
  toMemoryProposedAction,
  useMemoryProposalDecision,
  usePendingMemoryProposals,
} from '../chats/useMemoryProposals';
import {
  toSkillProposedAction,
  usePendingSkillProposals,
  useSkillProposalDecision,
} from '../chats/useSkillProposals';
import {
  toProposedAction,
  usePendingTemplateProposals,
  useTemplateProposalDecision,
} from '../chats/useTemplateProposals';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { toEmailDraft, useConversationEmailDrafts } from '../chats/useEmailDrafts';
import { useOpenConversation } from '../chats/useOpenConversation';
import { ChatFile, UploadingChatFile } from '../chats/types';
import { FileRejection } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { v4 as uuidv4 } from 'uuid';
import { buildEditActionTelemetry } from './editActionTelemetry';
import { resolveDocumentEditOwner } from 'src/document-editor/documentOwner';

// Below this floor the content region stops shrinking and the whole panel column scrolls instead,
// so the input and action cards can never be clipped out of reach on a short viewport.
const MIN_CHAT_CONTENT_HEIGHT = rem(140);
const SHORT_VIEWPORT_QUERY = '(max-height: 720px)';

export function SiestaBar() {
  const { siestaMode, setSiestaMode, siestaBusyGlowTop } = useAppContainer();
  const { contentMeta, conversationId, sidebarFullScreen } = useAppContainerContext();
  const emptyMessage = useChatEmptyMessage();
  const { recordAction } = useRecordEditAction();
  const {
    flagEnabledSiestaSteering,
    flagEnabledSiestaClientDataEditing,
    flagEnabledMeetingActions,
    flagEnabledSiestaSuitabilityReportProposals,
    flagEnabledSiestaOutlookEmailActions,
    flagEnabledSiestaEmailGeneratedAttachments,
    flagEnabledSiestaOutlookEmailReading,
    flagEnabledSiestaMemoryProposals,
    flagEnabledSiestaSkillProposals,
    flagEnabledSiestaSaveToDocuments,
    flagEnabledSiestaSlashSkillCommands,
  } = useFlags();
  const { isSystemAdmin, isServiceAdmin } = useCurrentUser();

  const {
    messages,
    latestIncomingId,
    currentChatState,
    isReconnecting,
    isLoadingHistory,
    sendUserMessage,
    retryErroredSend,
    discardChat,
    resetIfDisplaced,
    previousChatContext,
    goBack,
    adoptDisplacedChat,
    markActionUsed,
    usedActionIds,
    finaliseLimitReachedMessage,
    markProposalDecided,
    supersedePendingProposals,
    stopGeneration,
    chatScope,
  } = useSiesta();
  const queryClient = useQueryClient();
  const { clientId, familyId, focusedClientId } = chatScope;
  const historyScope = useHistoryScope();
  const { moveConversationToFamily } = useMoveConversationToFamily();

  // Dismiss the banner regardless: if the move fails the thread simply carries on filed against the
  // client it started from, which is still a working conversation.
  const handleMoveToFamily = () => {
    if (familyId) moveConversationToFamily(conversationId, familyId);
    adoptDisplacedChat();
  };

  // A new user message expires any still-pending proposal server-side at turn start; reflect
  // that immediately in already-rendered widgets and drop the cached pending lists so orphan
  // widgets do not keep live approve/deny buttons while the turn is processing.
  const expirePendingProposalWidgets = (activeConversationId: string) => {
    supersedePendingProposals();
    queryClient.setQueryData(
      ['conversations', activeConversationId, 'template-proposals', { status: 'PENDING' }],
      [],
    );
    queryClient.setQueryData(
      ['conversations', activeConversationId, 'client-data-proposals', { status: 'PENDING' }],
      [],
    );
    queryClient.setQueryData(
      ['conversations', activeConversationId, 'memory-proposals', { status: 'PENDING' }],
      [],
    );
    queryClient.setQueryData(
      ['conversations', activeConversationId, 'skill-proposals', { status: 'PENDING' }],
      [],
    );
  };

  const isProcessing = currentChatState !== null;
  const segmentValue: ChatBarTab = siestaMode === 'busy' ? 'action' : siestaMode;
  const compactSteering = !flagEnabledSiestaSteering;
  const shortViewport = useMediaQuery(SHORT_VIEWPORT_QUERY) ?? false;

  const { data: conversations = [] } = useConversations(
    historyScope.clientId,
    historyScope.familyId,
  );
  const reopenConversation = useOpenConversation();
  const openConversation = conversations.find(conversation => conversation.id === conversationId);
  const historyHasActivity = conversations.some(
    conversation =>
      conversation.id !== conversationId &&
      (conversation.turnState === 'UNREAD' || conversation.turnState === 'PROCESSING'),
  );
  const conversationHasActivity =
    siestaMode !== 'chat' &&
    openConversation !== undefined &&
    (openConversation.turnState === 'PROCESSING' || openConversation.turnState === 'UNREAD');

  // The chat view renders in-memory messages and stops listening when the user leaves the tab, so a
  // turn that finishes (or is still generating) in the background while they are away is not
  // reflected. Coming back to the conversation reloads it from the server, the same path the
  // history row uses, so the completed answer shows and an in-flight turn resumes streaming.
  const reconcileOpenConversation = () => {
    if (
      conversationId &&
      !isProcessing &&
      openConversation &&
      (openConversation.turnState === 'PROCESSING' || openConversation.turnState === 'UNREAD')
    ) {
      reopenConversation(conversationId);
    }
  };

  const handleTabChange = (next: ChatBarTab) => {
    setSiestaMode(next);
    if (next === 'chat') reconcileOpenConversation();
  };

  const { scrollRef, contentRef, handleScroll, handleContentInteraction, forceScrollToBottom } =
    useChatScroll(conversationId);

  const { actions: siestaActions, isLoading: isLoadingActions } = useSuggestedActions({
    documentId: contentMeta?.entityId,
    documentContent: contentMeta?.metadata?.documentContent,
    clientId: clientId ?? undefined,
    focusedClientId: focusedClientId ?? undefined,
    familyId: familyId ?? undefined,
    conversationId,
  });

  const visibleActions = siestaActions.filter(a => !usedActionIds.has(a.id));

  const { uploadChatFiles, isUploading, deleteChatFile } = useChatFiles(conversationId);
  const { data: chatFileTypes } = useChatFileTypes();
  const [pendingFiles, setPendingFiles] = useState<ChatFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingChatFile[]>([]);
  const { data: chatSkills } = useChatSkills();
  const [selectedSkill, setSelectedSkill] = useState<ChatSkill | null>(null);
  const {
    enabled: thinkingEffortEnabled,
    thinkingEffort,
    chooseThinkingEffort,
    effortToSend,
    carryChosenEffortInto,
  } = useConversationThinkingEffort();

  useEffect(() => {
    setPendingFiles([]);
    setUploadingFiles([]);
    setSelectedSkill(null);
  }, [conversationId]);

  const notifyFilesRejected = (rejections: ChatFileRejection[]) => {
    if (!chatFileTypes || rejections.length === 0) return;
    if (rejections.length === 1) {
      notifications.show({
        title: `Could not attach ${rejections[0].filename}`,
        message: chatFileRejectionMessage(rejections[0].reason, chatFileTypes),
        color: 'red',
      });
      return;
    }
    notifications.show({
      title: `Could not attach ${rejections.length} files`,
      message: chatFileRejectionsMessage(rejections, chatFileTypes),
      color: 'red',
    });
  };

  const handleAttachFiles = (files: File[]) => {
    const rejections: ChatFileRejection[] = [];
    const accepted = chatFileTypes
      ? files.filter(file => {
          const reason = chatFileRejection(file, chatFileTypes);
          if (reason) rejections.push({ filename: file.name, reason });
          return reason === null;
        })
      : files;
    notifyFilesRejected(rejections);
    if (!accepted.length) return;

    const uploads = accepted.map(file => ({
      id: uuidv4(),
      filename: file.name,
      extension: chatFileExtension(file.name).slice(1),
    }));
    setUploadingFiles(prev => [...prev, ...uploads]);
    const uploadIds = new Set(uploads.map(upload => upload.id));

    void uploadChatFiles(accepted)
      .then(created => setPendingFiles(prev => [...prev, ...created]))
      .finally(() => setUploadingFiles(prev => prev.filter(file => !uploadIds.has(file.id))));
  };

  const handleRemovePendingFile = (file: ChatFile) => {
    setPendingFiles(prev => prev.filter(pending => pending.id !== file.id));
    void deleteChatFile(file.id).catch(() => {
      setPendingFiles(prev => [...prev, file]);
      notifications.show({
        title: `Could not remove ${file.filename}`,
        message: 'Please try again.',
        color: 'red',
      });
    });
  };

  const handleRejectFiles = (rejections: FileRejection[]) => {
    notifyFilesRejected(
      rejections.map((rejection): ChatFileRejection => {
        const tooLarge = rejection.errors.some(error => error.code === 'file-too-large');
        return { filename: rejection.file.name, reason: tooLarge ? 'size' : 'type' };
      }),
    );
  };

  // Every message the sidebar sends goes at the level chosen for its chat, the tapped ones included,
  // so a chat set to minimal does not answer a follow-up chip at maximum.
  const sendAtChosenEffort = (
    content: string,
    sendClientId: string | null,
    activeConversationId: string,
    options: { attachedFiles?: ChatFile[]; silent?: boolean; skill?: MessageSkill } = {},
  ) => {
    carryChosenEffortInto(activeConversationId);
    return sendUserMessage(
      content,
      sendClientId,
      activeConversationId,
      options.attachedFiles ?? [],
      undefined,
      options.silent ?? false,
      undefined,
      options.skill,
      effortToSend,
    );
  };

  const handleActionClick = async (action: SiestaAction) => {
    if (action.type !== SiestaActionType.CHAT) return;

    const activeConversationId = resetIfDisplaced();
    markActionUsed(action.id);
    setSiestaMode('chat');
    forceScrollToBottom();

    await sendAtChosenEffort(action.prompt ?? action.label, clientId ?? null, activeConversationId);

    if (contentMeta?.entityId) {
      recordAction(
        buildEditActionTelemetry({
          documentId: contentMeta.entityId,
          actionText: action.label,
          source: 'SUGGESTED',
          conversationId: activeConversationId,
          owner: resolveDocumentEditOwner(clientId, familyId),
        }),
      );
    }
  };

  const handleInputSubmit = async (message: string) => {
    const activeConversationId = resetIfDisplaced();
    setSiestaMode('chat');
    const attachedFiles = pendingFiles;
    const skill = selectedSkill
      ? { name: selectedSkill.name, label: selectedSkill.label }
      : undefined;
    setPendingFiles([]);
    setSelectedSkill(null);
    expirePendingProposalWidgets(activeConversationId);
    forceScrollToBottom();
    await sendAtChosenEffort(message, clientId ?? null, activeConversationId, {
      attachedFiles,
      skill,
    });
  };

  const handleRetry = async (marker: ErrorMarker) => {
    forceScrollToBottom();
    await retryErroredSend(marker);
  };

  // A suggested next step is sent exactly as if the user had typed it.
  const handleFollowUp = async (text: string) => {
    const activeConversationId = resetIfDisplaced();
    setSiestaMode('chat');
    forceScrollToBottom();
    await sendAtChosenEffort(text, clientId ?? null, activeConversationId);
  };

  const handleContinue = async (widgetMessageId: string) => {
    const activeConversationId = resetIfDisplaced();
    setSiestaMode('chat');
    finaliseLimitReachedMessage(widgetMessageId);
    expirePendingProposalWidgets(activeConversationId);
    forceScrollToBottom();
    await sendAtChosenEffort('Continue', clientId ?? null, activeConversationId);
  };

  const { pendingProposals } = usePendingTemplateProposals(
    conversationId,
    !isProcessing &&
      (isSystemAdmin || isServiceAdmin) &&
      (openConversation !== undefined || messages.length > 0),
  );
  const { decideProposal, isDeciding } = useTemplateProposalDecision(conversationId);
  // Flags read as undefined until LaunchDarkly loads, and useQuery treats an undefined `enabled`
  // as on, so a gate built from `flag && ...` polls a flag-gated endpoint before the flag is known.
  const { pendingProposals: pendingClientDataProposals } = usePendingClientDataProposals(
    conversationId,
    flagEnabledSiestaClientDataEditing === true && !isProcessing,
  );
  const { decideClientDataProposal, isDecidingClientData } =
    useClientDataProposalDecision(conversationId);
  const { pendingProposals: pendingMemoryProposals } = usePendingMemoryProposals(
    conversationId,
    flagEnabledSiestaMemoryProposals === true && !isProcessing,
  );
  const { decideMemoryProposal, isDecidingMemory } = useMemoryProposalDecision(conversationId);
  const { pendingProposals: pendingSkillProposals } = usePendingSkillProposals(
    conversationId,
    flagEnabledSiestaSkillProposals === true && isSystemAdmin && !isProcessing,
  );
  const { decideSkillProposal, isDecidingSkill } = useSkillProposalDecision(conversationId);
  const { decideMeetingActionProposal, isDecidingMeetingAction } =
    useMeetingActionProposals(conversationId);
  const { pendingMeetingActionProposals } = usePendingMeetingActionProposals(
    conversationId,
    flagEnabledMeetingActions === true && !isProcessing,
  );
  const {
    decideSuitabilityReportProposal,
    isDecidingSuitabilityReport,
    fetchSuitabilityReportProposal,
  } = useSuitabilityReportProposals(conversationId);
  const { suitabilityReportProposals } = useSuitabilityReportProposalList(
    conversationId,
    flagEnabledSiestaSuitabilityReportProposals === true && !isProcessing,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { family: chatFamily, isLoading: isChatFamilyLoading } = useFamily(
    clientId ? undefined : (familyId ?? undefined),
  );
  // The flow opens under a client even for a household; on the household page that is the member
  // the page is showing, else the household's main member.
  const suitabilityHostClientId = resolveSuitabilityHostClientId({
    routeClientId: clientId ?? null,
    memberParam: searchParams.get('member'),
    familyMainClientId: chatFamily?.mainFamilyMember.clientId ?? null,
    familyMemberIds: chatFamily?.members.map(member => member.clientId) ?? null,
  });
  // Approving hands the adviser to the generation flow, pre-filled, to review and generate there.
  const openSuitabilityFlow = (proposal: Parameters<typeof suitabilityFlowLink>[0]) => {
    const link = suitabilityFlowLink(proposal, suitabilityHostClientId, location.pathname);
    if (!link) {
      notifications.show({
        title: 'Open a client to review this report',
        message: 'Go to one of the household’s clients, then choose Review report again.',
      });
      return;
    }
    navigate(link.to, { state: link.state });
  };
  const handleSuitabilityReview = (proposalId: string) => {
    const proposal = suitabilityReportProposals.find(p => p.proposalId === proposalId);
    if (!proposal?.templateId) return;
    openSuitabilityFlow({
      clientId: proposal.clientId ?? null,
      familyId: proposal.familyId ?? null,
      templateId: proposal.templateId,
      reportFormat: proposal.reportFormat,
      documents: proposal.documents,
    });
  };
  // The sidebar never unmounts, so `enabled` flipping over each turn (via isProcessing) is
  // what re-fetches this after a turn completes — same pattern as the proposal polls.
  const { savedSourceKeys, saveOwner } = useSavedPresentedFiles(
    conversationId,
    flagEnabledSiestaSaveToDocuments === true && !isProcessing,
  );
  const proposalIdsInMessages = new Set(
    messages.flatMap(message =>
      isChatMessage(message)
        ? (message.proposedActions ?? []).map(proposal => proposal.proposalId)
        : [],
    ),
  );
  const { recordOrphanDecision, applyOrphanDecisions } = useOrphanProposalDecisions();
  const { canApprove: canApproveSuitabilityReport, settleConflict: settleSuitabilityConflict } =
    useSuitabilityDecisionGuards({
      proposals: suitabilityReportProposals,
      hostClientId: suitabilityHostClientId,
      fetchProposal: fetchSuitabilityReportProposal,
      markDecided: markProposalDecided,
      recordOrphan: recordOrphanDecision,
    });
  const orphanProposals: ProposedAction[] = applyOrphanDecisions([
    ...pendingProposals.map(toProposedAction).filter(p => !proposalIdsInMessages.has(p.proposalId)),
    ...(flagEnabledSiestaClientDataEditing
      ? pendingClientDataProposals
          .map(toClientDataProposedAction)
          .filter(p => !proposalIdsInMessages.has(p.proposalId))
      : []),
    ...(flagEnabledSiestaMemoryProposals
      ? pendingMemoryProposals
          .map(toMemoryProposedAction)
          .filter(p => !proposalIdsInMessages.has(p.proposalId))
      : []),
    ...(flagEnabledSiestaSkillProposals
      ? pendingSkillProposals
          .map(toSkillProposedAction)
          .filter(p => !proposalIdsInMessages.has(p.proposalId))
      : []),
    ...(flagEnabledMeetingActions
      ? pendingMeetingActionProposals
          .map(toMeetingActionProposedAction)
          .filter(p => !proposalIdsInMessages.has(p.proposalId))
      : []),
    ...(flagEnabledSiestaSuitabilityReportProposals
      ? suitabilityReportProposals.filter(p => !proposalIdsInMessages.has(p.proposalId))
      : []),
  ]);

  const outlookEnabled = flagEnabledSiestaOutlookEmailActions;
  // Email reading has its own flag and its own connect surface (the Connect Outlook card),
  // so the connection status must also be read when only reading is enabled.
  const { connected: outlookConnected } = useMicrosoftEmailConnectionStatus(
    outlookEnabled === true || flagEnabledSiestaOutlookEmailReading === true,
  );
  // A Connect Outlook card on the newest message means a paused email request is waiting on
  // the connection, so connecting from anywhere — including an email draft card — should
  // resume the conversation. Anything the user has since replied to is no longer waiting.
  const newestChatMessage = messages.filter(isChatMessage).at(-1);
  const outlookConnectRequested = Boolean(newestChatMessage?.outlookConnectRequests?.length);
  const outlookEmailContext: OutlookEmailContext = {
    enabled: outlookEnabled,
    connected: outlookConnected,
    connectRequested: outlookConnectRequested,
    generatedAttachmentsEnabled: outlookEnabled && flagEnabledSiestaEmailGeneratedAttachments,
    conversationId,
    clientId: clientId ?? undefined,
    familyId: familyId ?? undefined,
    pageSetsContentMeta: contentMeta !== null,
  };

  const { emailDrafts } = useConversationEmailDrafts(conversationId, !isProcessing);
  const draftIdsInMessages = new Set(
    messages.flatMap(message =>
      isChatMessage(message) ? (message.emailDrafts ?? []).map(draft => draft.draftId) : [],
    ),
  );
  const orphanEmailDrafts = emailDrafts
    .map(toEmailDraft)
    .filter(d => !draftIdsInMessages.has(d.draftId));

  // Recovered drafts belong to the loaded history, not the live turn — anchor them at the
  // end of the history block so new messages appear below them, not above. Stored messages
  // carry no timestamps, so this is the closest available approximation of their true
  // position. Re-anchored every time a history load completes (open, reopen, reconcile).
  const [orphanAnchorIndex, setOrphanAnchorIndex] = useState(() => messages.length);
  const wasLoadingHistoryRef = useRef(isLoadingHistory);
  useEffect(() => {
    if (wasLoadingHistoryRef.current && !isLoadingHistory) {
      setOrphanAnchorIndex(messages.length);
    }
    wasLoadingHistoryRef.current = isLoadingHistory;
  }, [isLoadingHistory, messages.length]);

  const decideFor = (proposal: ProposedAction) => {
    if (isClientDataProposal(proposal)) {
      return decideClientDataProposal;
    }
    if (isMemoryProposal(proposal)) {
      return decideMemoryProposal;
    }
    if (isSkillProposal(proposal)) {
      return decideSkillProposal;
    }
    if (isMeetingActionProposal(proposal)) {
      return decideMeetingActionProposal;
    }
    if (isSuitabilityReportProposal(proposal)) {
      return decideSuitabilityReportProposal;
    }
    return decideProposal;
  };

  const handleProposalDecision = async (
    widgetMessageId: string | null,
    proposal: ProposedAction,
    approve: boolean,
  ) => {
    if (
      approve &&
      isSuitabilityReportProposal(proposal) &&
      !canApproveSuitabilityReport(proposal)
    ) {
      return;
    }
    const activeConversationId = resetIfDisplaced();
    setSiestaMode('chat');
    // Hoisted out of the try block: the React Compiler cannot yet optimise value blocks
    // (ternaries, nullish coalescing) inside try/catch.
    const decision = approve ? 'APPROVE' : 'DENY';
    const resumeClientId = clientId ?? null;
    const decide = decideFor(proposal);
    let outcome;
    try {
      const { data } = await decide({ proposalId: proposal.proposalId, decision });
      outcome = data;
    } catch (error) {
      Sentry.captureException(error);
      // Only a 409/404 means the proposal is really gone (expired or superseded server-side);
      // any other failure is transient and the widget must stay pending so the user can retry.
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        isSuitabilityReportProposal(proposal)
      ) {
        await settleSuitabilityConflict(widgetMessageId, proposal.proposalId);
      } else if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
        if (widgetMessageId) {
          markProposalDecided(widgetMessageId, proposal.proposalId, 'SUPERSEDED');
        } else {
          recordOrphanDecision(proposal.proposalId, 'SUPERSEDED');
        }
        notifications.show({
          title: 'This proposal has expired',
          message: 'Ask Siesta to propose the change again.',
          color: 'red',
        });
      } else {
        notifications.show({
          title: 'Could not submit your decision',
          message: 'Something went wrong — please try again.',
          color: 'red',
        });
      }
      return;
    }
    const owner =
      isSuitabilityReportProposal(proposal) && 'clientId' in outcome && 'familyId' in outcome
        ? { clientId: outcome.clientId, familyId: outcome.familyId }
        : undefined;
    if (widgetMessageId) {
      markProposalDecided(
        widgetMessageId,
        proposal.proposalId,
        outcome.status,
        outcome.failureReason,
        owner,
      );
    } else {
      recordOrphanDecision(proposal.proposalId, outcome.status, outcome.failureReason, owner);
    }
    // Started before navigating: sending resolves only once Siesta's reply has streamed, which
    // held the adviser on the chat for up to 20 s. The chat state lives above this sidebar, so
    // the reply still lands after the flow's page replaces it.
    const resumeMessage = resumeMessageFor(
      approve,
      outcome.status,
      isSuitabilityReportProposal(proposal),
    );
    forceScrollToBottom();
    const reply = sendAtChosenEffort(resumeMessage, resumeClientId, activeConversationId);
    if (
      isSuitabilityReportProposal(proposal) &&
      outcome.status === 'APPROVED' &&
      'reportFormat' in outcome
    ) {
      openSuitabilityFlow(outcome);
    }
    try {
      // The resume message lets Siesta react to the outcome; the decision itself was already
      // applied server-side above, so a failed send only delays the reaction.
      await reply;
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleVerifyAction = async (outcome: VerifyOutcome, reply?: string) => {
    const activeConversationId = resetIfDisplaced();
    const finding = reply?.trim() ? ` Here is what you found: ${reply.trim()}` : '';
    // Name the target: "an edit" alone reads as a client-data proposal and gets refused.
    const instruction =
      outcome === 'correction'
        ? `Please edit the document to correct the statement you just selected, based on what you found. Change the document text only — do not propose any change to the client's records.${finding}`
        : `Please remove the statement you just selected from the document.${finding}`;
    // Silent: the instruction is injected by the offer button, not typed by the user, so only the
    // thinking state and the assistant's reply show — no instruction bubble.
    forceScrollToBottom();
    await sendAtChosenEffort(instruction, clientId ?? null, activeConversationId, { silent: true });
  };

  const handleInputFocus = () => {
    // Move to the conversation before typing, from any other tab, so a message is never added to
    // an open chat the user cannot see (e.g. while browsing History).
    if (siestaMode !== 'chat') {
      setSiestaMode('chat');
      reconcileOpenConversation();
    }
  };

  const handleNewChat = () => {
    discardChat();
    setSiestaMode('chat');
  };

  const isSteering = siestaMode === 'action';
  const isEmptyChat = siestaMode === 'chat' && messages.length === 0;
  const flameHeight = resolveFlameHeight(isSteering, isEmptyChat);
  const flameOpacity = isSteering || isEmptyChat ? 0.75 : 0.3;

  let messagesContent;
  if (isLoadingHistory) {
    messagesContent = (
      <Center h="100%">
        <Loader color="white" />
      </Center>
    );
  } else if (messages.length === 0) {
    messagesContent = (
      <ChatEmptyState title={emptyMessage.title} subtitle={emptyMessage.subtitle} dark />
    );
  } else {
    messagesContent = (
      <Box
        ref={contentRef}
        onPointerDown={handleContentInteraction}
        onKeyDown={handleContentInteraction}
      >
        <SiestaBarMessages
          messages={messages}
          latestIncomingId={latestIncomingId ?? undefined}
          isStreaming={isProcessing}
          isReconnecting={isReconnecting}
          onContinue={handleContinue}
          onProposalDecision={handleProposalDecision}
          onSuitabilityReview={handleSuitabilityReview}
          isSuitabilityHostLoading={isChatFamilyLoading}
          orphanProposals={orphanProposals}
          orphanEmailDrafts={orphanEmailDrafts}
          outlook={outlookEmailContext}
          orphanAnchorIndex={orphanAnchorIndex}
          isDecidingProposal={
            isDeciding ||
            isDecidingClientData ||
            isDecidingMemory ||
            isDecidingSkill ||
            isDecidingMeetingAction ||
            isDecidingSuitabilityReport
          }
          onVerifyAction={handleVerifyAction}
          onRetry={handleRetry}
          dark
          conversationId={conversationId}
          presentedFileSaveContext={saveOwner ? { owner: saveOwner, savedSourceKeys } : undefined}
          onFollowUp={text => void handleFollowUp(text)}
        />
      </Box>
    );
  }

  const chatContent = (
    <Box pos="relative" flex={1} mih={MIN_CHAT_CONTENT_HEIGHT}>
      <Transition mounted={siestaMode === 'busy'} transition="fade" duration={200} keepMounted>
        {styles => (
          <Box
            className={classes.busyContainer}
            px="xxxl"
            pos="absolute"
            top="xl"
            style={{ ...styles, overflow: 'hidden', inset: 0 }}
          >
            <SiestaBusyGlow top={siestaBusyGlowTop ?? '50%'} />
            <Text size="textXl" fw={FONT_WEIGHTS.semibold} c="#333" className={classes.busyText}>
              {'Siesta is busy,\nfinish the action to turn it back on'}
            </Text>
          </Box>
        )}
      </Transition>

      <Transition mounted={siestaMode === 'chat'} transition="fade" duration={200}>
        {styles => (
          <Flex
            direction="column"
            h="100%"
            pos="absolute"
            px="xl"
            pt="md"
            pb="md"
            style={{ ...styles, inset: 0 }}
          >
            {messages.length > 0 && (
              <Group justify="flex-end">
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  aria-label="Start a new chat"
                  onClick={handleNewChat}
                >
                  <IconPencilPlus size={22} color="var(--mantine-color-white)" />
                </ActionIcon>
              </Group>
            )}

            <Box pos="relative" flex={1} mih={0}>
              <ChatScrollBlur dark />
              <Box
                ref={scrollRef}
                onScroll={handleScroll}
                h="100%"
                pt={shortViewport ? 'md' : 'xxxxl'}
                // Browser scroll anchoring moves the view up when the status line above a streaming
                // answer disappears, which the chat would read as the reader scrolling away.
                style={{ overflow: 'auto', overscrollBehavior: 'contain', overflowAnchor: 'none' }}
              >
                {messagesContent}
              </Box>
            </Box>

            <DisplacedChatBanner
              previousChatContext={previousChatContext}
              onGoBack={goBack}
              onMoveToFamily={handleMoveToFamily}
            />
          </Flex>
        )}
      </Transition>

      <Transition mounted={siestaMode === 'action'} transition="fade" duration={200} keepMounted>
        {styles => (
          <Box
            pos="absolute"
            px="xl"
            pt={compactSteering || shortViewport ? 'md' : 'xxxl'}
            pb={shortViewport ? 'md' : 'xxxl'}
            style={{ ...styles, inset: 0 }}
          >
            <Flex h="100%" direction="column">
              <ActionModeScrollArea>
                <ActionModeContent
                  isLoadingActions={isLoadingActions}
                  visibleActions={visibleActions}
                  onActionClick={handleActionClick}
                  compact={compactSteering}
                />
              </ActionModeScrollArea>
            </Flex>
          </Box>
        )}
      </Transition>

      <Transition mounted={siestaMode === 'history'} transition="fade" duration={200} keepMounted>
        {styles => (
          <Box pos="absolute" px="xl" pt="md" pb="md" style={{ ...styles, inset: 0 }}>
            <ChatsView onNewChat={handleNewChat} />
          </Box>
        )}
      </Transition>
    </Box>
  );

  return (
    <Box pos="relative" h="100%" style={{ overflow: 'hidden', borderRadius: 'inherit' }}>
      <Box className={classes.chatSurface} />
      <Box className={classes.chatFlames} style={{ height: flameHeight }}>
        <MaskedSidebarVideo opacity={flameOpacity} scaleX={4} />
      </Box>

      <Flex
        direction="column"
        h="100%"
        pos="relative"
        style={{ zIndex: 2, minHeight: 0, overflowY: 'auto' }}
      >
        {(siestaMode !== 'busy' || sidebarFullScreen) && (
          <Flex px="xl" pt="md" gap="xs" align="center">
            <ExitFullScreenPill style={{ flexShrink: 0 }} />
            {siestaMode !== 'busy' && (
              <Box flex={1} miw={0}>
                <ChatModeTabs
                  value={segmentValue}
                  onChange={handleTabChange}
                  historyHasActivity={historyHasActivity}
                  conversationHasActivity={conversationHasActivity}
                />
              </Box>
            )}
          </Flex>
        )}

        <ChatFileDropzone
          onDrop={handleAttachFiles}
          onReject={handleRejectFiles}
          siestaMode={siestaMode}
          fileTypes={chatFileTypes}
        >
          {chatContent}
          {siestaMode !== 'busy' && (
            <SiestaChatInput
              conversationId={conversationId}
              siestaMode={siestaMode}
              pendingFiles={pendingFiles}
              uploadingFiles={uploadingFiles}
              fileTypes={chatFileTypes}
              onSubmit={handleInputSubmit}
              onFocus={handleInputFocus}
              isProcessing={isProcessing}
              onStop={stopGeneration}
              onAttachFiles={handleAttachFiles}
              onRemovePendingFile={handleRemovePendingFile}
              isUploading={isUploading}
              dense={shortViewport}
              skills={flagEnabledSiestaSlashSkillCommands === true ? (chatSkills ?? []) : undefined}
              selectedSkill={selectedSkill}
              onSelectSkill={setSelectedSkill}
              thinkingEffort={thinkingEffortEnabled ? thinkingEffort : undefined}
              onThinkingEffortChange={thinkingEffortEnabled ? chooseThinkingEffort : undefined}
            />
          )}
        </ChatFileDropzone>
      </Flex>
    </Box>
  );
}

function resolveFlameHeight(isSteering: boolean, isEmptyChat: boolean): string {
  if (isSteering) return '200%';
  if (isEmptyChat) return '100%';
  return '50%';
}

function ActionModeContent({
  isLoadingActions,
  visibleActions,
  onActionClick,
  compact,
}: {
  isLoadingActions: boolean;
  visibleActions: SiestaAction[];
  onActionClick: (action: SiestaAction) => void;
  compact: boolean;
}) {
  const { flagEnabledSiestaSteering } = useFlags();

  if (flagEnabledSiestaSteering) {
    return (
      <Flex flex={1} direction="column" style={{ minHeight: 0 }}>
        <SteeringActionContent suggestedActions={visibleActions} onActionClick={onActionClick} />
      </Flex>
    );
  }

  if (isLoadingActions) {
    return (
      <Center flex={1}>
        <Loader color="white" />
      </Center>
    );
  }

  return (
    <Flex flex={1} align={compact ? 'flex-start' : 'center'} justify="center">
      <AIActionSet actions={visibleActions} onActionClick={onActionClick} compact={compact} />
    </Flex>
  );
}
